import { SystemBars, SystemBarsStyle } from '@capacitor/core';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';

import { env } from '@/config/env';

export type ThemePreference = 'dark' | 'light' | 'system';
export type ResolvedTheme = 'dark' | 'light';

/**
 * Warning: MUST stay identical to the key read by the inline script in `index.html`,
 * which applies the theme before the first paint to avoid a flash.
 *
 * `localStorage` is read directly rather than through the async `lib/storage`:
 * reading synchronously is what guarantees the wrong theme is never shown, not
 * even for a frame. Its fragility — the OS may purge it — matters for an auth
 * token, not for a display preference.
 */
const THEME_STORAGE_KEY = 'meeply.theme';

const LIGHT_QUERY = '(prefers-color-scheme: light)';

interface ThemeContextValue {
  /** What the reader chose, `system` included. */
  preference: ThemePreference;
  /** What is actually applied to the document. */
  theme: ResolvedTheme;
  setPreference: (preference: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readStoredPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'dark' || stored === 'light' || stored === 'system') {
      return stored;
    }
  } catch {
    /* private browsing: fall back to the default */
  }
  return 'system';
}

/* The system theme is an EXTERNAL state: subscribe to it, never copy it into
   a `useState`. Copying would force a `setState` inside an effect, and with it
   a cascading render on every mount. */
function subscribeToSystemTheme(onChange: () => void) {
  const media = globalThis.matchMedia(LIGHT_QUERY);
  media.addEventListener('change', onChange);
  return () => media.removeEventListener('change', onChange);
}

const getSystemTheme = (): ResolvedTheme =>
  globalThis.matchMedia(LIGHT_QUERY).matches ? 'light' : 'dark';

/**
 * Tells the system bars what they are sitting on.
 *
 * The clock and the battery are drawn over the page now, so their colour is
 * ours to get right.
 *
 * ⚠️ The names read backwards: `Dark` is **light content on a dark
 * background**. Reading it as "make the bar dark" produces black icons on a
 * black background — a status bar that looks switched off.
 *
 * @param scheme What the top of the screen actually looks like.
 */
function applySystemBars(scheme: ResolvedTheme): void {
  // Written whatever the platform: it is what the value can be read back from,
  // and the only trace of a call that otherwise leaves none.
  globalThis.document.documentElement.dataset.bars = scheme;

  if (!env.isNative) return;

  void SystemBars.setStyle({
    style: scheme === 'dark' ? SystemBarsStyle.Dark : SystemBarsStyle.Light,
  }).catch(() => {
    /* An older shell without the plugin keeps the system's own choice. */
  });
}

/**
 * How many mounted screens are currently claiming their own appearance.
 *
 * ⚠️ A counter, not a boolean. Moving between two screens that both claim it —
 * one legal document to another — mounts the new one **before** unmounting the
 * old, because the route is lazy and Suspense keeps the old tree alive while
 * the chunk loads. A cleanup that restored unconditionally would therefore run
 * last and undo the arrival.
 */
let claims = 0;

/**
 * Overrides the system bars for a screen that paints its own background.
 *
 * ⚠️ The legal documents are white **in both themes**, and they set that on
 * themselves rather than on the document root — so the application's theme
 * still says "dark" while the screen is white, and the bars would carry white
 * icons onto white paper. The clock simply vanishes.
 *
 * Restores the application's own appearance on the way out, which nothing else
 * would do: the provider's effect only fires when the theme itself changes.
 *
 * ⚠️ The value to restore is read from the document rather than from
 * `useTheme`. It is the same source — the provider writes it there — and it
 * keeps a screen from needing the provider mounted above it just to say what
 * colour its own status bar is. The legal pages are rendered on their own in
 * their tests, and would have thrown.
 *
 * @param scheme What this screen looks like at the top.
 */
export function useSystemBarsAppearance(scheme: ResolvedTheme): void {
  useEffect(() => {
    claims += 1;
    applySystemBars(scheme);

    return () => {
      claims -= 1;
      if (claims > 0) return;

      const applied = globalThis.document.documentElement.dataset.theme;
      applySystemBars(applied === 'light' ? 'light' : 'dark');
    };
  }, [scheme]);
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] =
    useState<ThemePreference>(readStoredPreference);

  const systemTheme = useSyncExternalStore(
    subscribeToSystemTheme,
    getSystemTheme,
    () => 'dark' as const,
  );

  // A derived value, not a state: it cannot fall out of sync.
  const theme: ResolvedTheme =
    preference === 'system' ? systemTheme : preference;

  // Push the value to the two external systems that hold it. `[data-theme]` is
  // what every semantic token of theme.css reads — no component has to care.
  useEffect(() => {
    document.documentElement.dataset.theme = theme;

    /*
     * ⚠️ Driven by the application's theme, not the device's. The two agree
     * until somebody picks a theme by hand, and that is exactly the moment
     * `DEFAULT` would get it wrong.
     *
     * ⚠️ Skipped while a screen claims its own appearance. Effects run
     * child-first: on a cold load straight onto a legal document — a deep
     * link, a shared address — the document asks for light bars and this
     * provider, mounted above it, would answer dark a moment later. White
     * icons on white paper, and the clock disappears.
     */
    if (claims === 0) applySystemBars(theme);
  }, [theme]);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      /* preference not persisted: harmless */
    }
  }, []);

  const value = useMemo(
    () => ({ preference, theme, setPreference }),
    [preference, theme, setPreference],
  );

  return <ThemeContext value={value}>{children}</ThemeContext>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme doit être utilisé dans un <ThemeProvider>.');
  }
  return context;
}
